import React, { useState } from 'react';
import { 
  MessageSquare, 
  HelpCircle, 
  FileText, 
  ShieldCheck, 
  Info, 
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Download,
  Smartphone,
  Share,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'motion/react';
import { Badge } from '@/components/ui/badge';

export default function HelpCenter() {
  const [activeTab, setActiveTab] = useState('kontak');

  const tabs = [
    { id: 'kontak', label: 'Hubungi Kami', icon: MessageSquare, description: 'Bantuan langsung & konsultasi' },
    { id: 'bantuan', label: 'Pusat Bantuan', icon: HelpCircle, description: 'Tanya jawab seputar layanan' },
    { id: 'instalasi', label: 'Panduan Instalasi', icon: Download, description: 'Cara pasang aplikasi di HP' },
    { id: 'syarat', label: 'Syarat & Ketentuan', icon: FileText, description: 'Ketentuan penggunaan layanan' },
    { id: 'kebijakan', label: 'Kebijakan Privasi', icon: ShieldCheck, description: 'Cara kami menjaga data Anda' },
    { id: 'tentang', label: 'Tentang Kami', icon: Info, description: 'Mengenal lebih dekat Azman204' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'kontak':
        return (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-8"
          >
            <div className="flex flex-col gap-2">
              <Badge className="w-fit bg-gold/20 text-gold-dark dark:text-gold border-gold/30 font-bold">Respon Cepat</Badge>
              <h2 className="text-3xl md:text-4xl font-black text-foreground">Hubungi <span className="text-gold">Kami</span></h2>
              <p className="text-foreground/70 max-w-2xl leading-relaxed">Punya pertanyaan atau butuh konsultasi teknis? Tim ahli kami siap membantu Anda mendapatkan solusi terbaik.</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="space-y-4">
                {[
                  { icon: MapPin, label: 'Alamat Kantor', value: 'Jl. Soekarno-Hatta , Anambas, Riau, Indonesia', sub: 'Pusat Layanan Administrasi' },
                  { icon: Phone, label: 'WhatsApp Bisnis', value: '+62 812 6108 9400', sub: 'Tersedia 24/7 untuk darurat' },
                  { icon: Mail, label: 'Email Resmi', value: 'azmankoe916@gmail.com', sub: 'Korespondensi formal' }
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-5 bg-card/40 backdrop-blur-sm p-6 rounded-[2rem] border border-border/50 hover:border-gold/30 transition-all group"
                  >
                    <div className="h-14 w-14 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center shrink-0 border border-gold/10 group-hover:scale-110 transition-transform shadow-inner">
                      <item.icon className="text-gold h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-foreground/40 mb-1">{item.label}</p>
                      <p className="font-black text-foreground text-lg leading-tight mb-0.5">{item.value}</p>
                      <p className="text-xs text-foreground/50">{item.sub}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <Card className="border-none shadow-2xl shadow-gold/5 rounded-[2.5rem] bg-card/60 backdrop-blur-md overflow-hidden relative border border-border/30">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
                <CardContent className="p-8 md:p-10">
                  <h3 className="text-xl font-black text-foreground mb-6">Kirim Pesan Cepat</h3>
                  <form className="space-y-5">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-foreground/60 ml-1">Nama Lengkap</label>
                      <input className="w-full p-4 rounded-2xl border border-border/50 focus:border-gold outline-none bg-muted/30 text-foreground font-bold transition-all" placeholder="E.g. Ahmad Fauzi" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-foreground/60 ml-1">Email</label>
                      <input className="w-full p-4 rounded-2xl border border-border/50 focus:border-gold outline-none bg-muted/30 text-foreground font-bold transition-all" placeholder="ahmad@email.com" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-foreground/60 ml-1">Kebutuhan Anda</label>
                      <textarea className="w-full p-4 rounded-2xl border border-border/50 focus:border-gold outline-none h-32 bg-muted/30 text-foreground font-bold transition-all resize-none" placeholder="Tuliskan pesan Anda di sini..."></textarea>
                    </div>
                    <Button type="button" className="w-full bg-gold hover:bg-gold-light text-navy font-black h-14 rounded-2xl shadow-xl shadow-gold/20 transition-all active:scale-95 text-xs uppercase tracking-widest">
                      Kirim Pesan Sekarang
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* WhatsApp CTA Section */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-12 bg-gradient-to-br from-[#25D366]/20 to-[#128C7E]/10 border border-[#25D366]/30 rounded-[3rem] p-8 md:p-12 relative overflow-hidden group"
            >
              <div className="absolute -right-12 -top-12 text-[#25D366]/5 rotate-12 pointer-events-none group-hover:scale-110 transition-transform duration-700">
                <MessageSquare size={300} />
              </div>
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="text-center md:text-left max-w-md">
                  <Badge className="bg-[#25D366]/20 text-[#25D366] border-[#25D366]/30 mb-4 font-bold">Respon Tercepat</Badge>
                  <h2 className="text-3xl md:text-4xl font-black text-foreground mb-4">
                    Butuh Konsultasi <span className="text-[#25D366]">Langsung?</span>
                  </h2>
                  <p className="text-foreground/80 text-lg font-medium leading-relaxed">Admin kami ramah dan siap memberikan bantuan teknis via WhatsApp.</p>
                </div>
                <a 
                  href="https://wa.me/6281261089400?text=Halo%20Admin%20Azman204,%20saya%20ingin%20konsultasi%20tentang..."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full md:w-auto inline-flex items-center justify-center bg-[#25D366] hover:bg-[#128C7E] text-white font-black py-5 px-10 rounded-2xl shadow-2xl shadow-[#25D366]/40 transition-all hover:scale-105 active:scale-95 uppercase text-sm tracking-widest gap-3"
                >
                  Buka Chat WhatsApp
                  <Smartphone className="h-5 w-5" />
                </a>
              </div>
            </motion.div>
          </motion.div>
        );
      case 'bantuan':
        return (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-8"
          >
            <div className="flex flex-col gap-2">
              <Badge className="w-fit bg-gold/20 text-gold-dark dark:text-gold border-gold/30 font-bold">FAQ Terpopuler</Badge>
              <h2 className="text-3xl md:text-4xl font-black text-foreground">Pertanyaan <span className="text-gold">Umum</span></h2>
              <p className="text-foreground/70 max-w-2xl leading-relaxed">Cari jawaban cepat untuk kendala yang sering dialami oleh pengguna lain.</p>
            </div>

            <div className="space-y-4">
              {[
                { q: "Berapa lama proses pembuatan dokumen SBU?", a: "Waktu pemrosesan standar berkisar antara 3 hingga 7 hari kerja. Kami selalu mengusahakan proses tercepat tanpa mengurangi ketelitian validasi data." },
                { q: "Apakah dokumen yang diterbitkan legal?", a: "Tentu. Setiap dokumen diproses melalui kanal resmi kementerian terkait. Keaslian dapat diverifikasi langsung melalui barcode/QR yang tertera di dokumen." },
                { q: "Bagaimana sistem pembayarannya?", a: "Kami menggunakan gateway pembayaran aman meliputi Transfer Bank (BCA, Mandiri, BNI) dan QRIS untuk kemudahan transaksi Anda." },
                { q: "Ada bantuan untuk pengadaan tender?", a: "Ya, kami menyediakan paket khusus pendampingan dokumen tender konstruksi. Silakan hubungi admin melalui WhatsApp untuk detail paket." }
              ].map((faq, i) => (
                <motion.div 
                  key={i} 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-card/40 backdrop-blur-md border border-border/50 p-8 rounded-[2rem] hover:border-gold/30 transition-all group"
                >
                  <div className="flex items-start gap-5">
                    <div className="h-10 w-10 bg-gold/10 rounded-xl flex items-center justify-center shrink-0 border border-gold/10 group-hover:scale-110 transition-transform mt-0.5">
                      <HelpCircle className="h-5 w-5 text-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-foreground mb-3 leading-tight group-hover:text-gold transition-colors">{faq.q}</h3>
                      <p className="text-foreground/70 text-sm leading-relaxed font-medium">{faq.a}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        );
      case 'instalasi':
        return (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-8"
          >
            <div className="flex flex-col gap-2">
              <Badge className="w-fit bg-gold/20 text-gold-dark dark:text-gold border-gold/30 font-bold">PWA Guide</Badge>
              <h2 className="text-3xl md:text-4xl font-black text-foreground">Pasang <span className="text-gold">Aplikasi</span></h2>
              <p className="text-foreground/70 max-w-2xl leading-relaxed">Nikmati pengalaman lebih cepat dengan memasang shortcut aplikasi langsung di layar utama ponsel Anda.</p>
            </div>
            
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              {[
                {
                  title: 'Android (Google Chrome)',
                  icon: Smartphone,
                  steps: [
                    'Buka azman204.com di browser Chrome HP.',
                    'Ketuk ikon tiga titik di sudut kanan atas.',
                    'Pilih "Install app" atau "Tambahkan ke Layar Utama".',
                    'Konfirmasi dengan mengetuk "Install". Selesai!'
                  ]
                },
                {
                  title: 'iOS iPhone (Safari)',
                  icon: Share,
                  steps: [
                    'Buka azman204.com menggunakan Safari.',
                    'Ketuk ikon "Bagikan" (kotak dengan panah atas).',
                    'Scroll ke bawah, pilih "Add to Home Screen".',
                    'Ketuk "Add" di pojok kanan atas. Kamu hebat!'
                  ]
                }
              ].map((platform, i) => (
                <Card key={i} className="p-8 border-border/50 shadow-xl shadow-gold/5 bg-card/60 backdrop-blur-md rounded-[2.5rem] h-full flex flex-col relative overflow-hidden group">
                  <div className="absolute top-0 left-0 w-2 h-full bg-gold/10 group-hover:bg-gold/30 transition-colors" />
                  <div className="flex items-center gap-4 mb-8">
                    <div className="h-14 w-14 bg-gold/10 rounded-2xl flex items-center justify-center text-gold shadow-inner border border-gold/10">
                      <platform.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-black text-foreground">{platform.title}</h3>
                  </div>
                  <div className="space-y-5 flex-grow">
                    {platform.steps.map((step, idx) => (
                      <div key={idx} className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gold/20 text-gold font-black text-xs flex items-center justify-center mt-0.5 border border-gold/10 shadow-sm">
                          {idx + 1}
                        </div>
                        <p className="text-sm text-foreground/80 font-bold leading-relaxed">{step}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
            
            <div className="bg-gradient-to-r from-gold/10 via-gold/5 to-transparent p-8 rounded-[2.5rem] border border-gold/20 relative overflow-hidden">
                <div className="flex items-start gap-5 relative z-10">
                    <div className="h-10 w-10 bg-gold/20 rounded-xl flex items-center justify-center shrink-0 border border-gold/20">
                      <Info className="h-5 w-5 text-gold" />
                    </div>
                    <div>
                        <h4 className="font-black text-foreground text-lg mb-2">Mengapa memasang aplikasi?</h4>
                        <p className="text-sm text-foreground/70 leading-relaxed font-bold">
                            Shortcut ini menggunakan teknologi Progressive Web App (PWA) yang sangat ringan, menghemat kuota, dan memberikan performa secepat kilat. Aplikasi akan otomatis ter-update setiap kali ada fitur baru tanpa harus download ulang.
                        </p>
                    </div>
                </div>
            </div>
          </motion.div>
        );
      case 'syarat':
      case 'kebijakan':
      case 'tentang':
        const contentMap = {
          syarat: {
            title: 'Syarat & Ketentuan',
            sections: [
              { h: 'Persetujuan Layanan', p: 'Dengan mengakses Azman204, Anda secara sadar menyetujui seluruh ketentuan operasional yang berlaku tanpa terkecuali.' },
              { h: 'Keabsahan Data', p: 'Seluruh lampiran yang diunggah harus data asli. Penggunaan data palsu akan mengakibatkan pembatalan layanan secara sepihak tanpa pengembalian biaya.' },
              { h: 'Sistem Pembayaran', p: 'Layanan bersifat prabayar. Pekerjaan baru akan dimulai setelah validasi bukti transfer berhasil dilakukan oleh sistem kami.' }
            ]
          },
          kebijakan: {
            title: 'Kebijakan Privasi',
            sections: [
              { h: 'Keamanan Data', p: 'Kami menerapkan enkripsi end-to-end pada setiap dokumen sensitif seperti e-KTP, NPWP, dan SBU Perusahaan Anda.' },
              { h: 'No-Third Party', p: 'Data Anda murni hanya untuk kepentingan pengajuan legalitas ke instansi terkait dan tidak akan pernah diperjualbelikan.' },
              { h: 'Retensi Informasi', p: 'Anda berhak menarik data dari sistem kami kapan saja dengan mengajukan permohonan penghapusan melalui Admin.' }
            ]
          },
          tentang: {
            title: 'Tentang Kami',
            sections: [
              { h: 'Visi Perusahaan', p: 'Digitalisasi total administrasi konstruksi Indonesia untuk efisiensi nasional yang lebih baik.' },
              { h: 'Misi Kami', p: 'Menyediakan platform legalitas satu pintu yang transparan, terpercaya, dan profesional bagi para pebisnis konstruksi.' }
            ]
          }
        };
        const current = contentMap[activeTab as keyof typeof contentMap];
        return (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-8"
          >
            <div className="flex flex-col gap-2">
              <Badge className="w-fit bg-gold/20 text-gold-dark dark:text-gold border-gold/30 font-bold">Legalitas & Info</Badge>
              <h2 className="text-3xl md:text-4xl font-black text-foreground">{current.title.split(' & ')[0]} <span className="text-gold">{current.title.split(' & ')[1] || ''}</span></h2>
            </div>
            
            <div className="bg-card/40 backdrop-blur-md rounded-[3rem] p-10 border border-border/50 shadow-2xl shadow-gold/5 space-y-10">
              {current.sections.map((sec, i) => (
                <div key={i} className="group">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle2 className="h-5 w-5 text-gold opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    <h3 className="text-xl font-black text-foreground group-hover:text-gold transition-colors">{sec.h}</h3>
                  </div>
                  <p className="text-foreground/70 font-medium leading-relaxed pl-8 border-l-2 border-gold/10 group-hover:border-gold/30 transition-colors">
                    {sec.p}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="pt-32 pb-32 min-h-screen bg-background selection:bg-gold/30">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row gap-12">
          
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-80 shrink-0">
            <div className="sticky top-32 space-y-4">
              <div className="mb-8 pl-4">
                <h1 className="text-xs font-black uppercase tracking-[0.3em] text-foreground/30 mb-2">Pusat Bantuan</h1>
                <p className="text-2xl font-black text-foreground">Butuh <span className="text-gold">Solusi?</span></p>
              </div>
              
              <div className="bg-card/40 backdrop-blur-xl rounded-[2.5rem] border border-border/50 p-3 shadow-xl shadow-gold/5">
                <div className="flex flex-col gap-1.5">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center justify-between w-full p-4 rounded-2xl transition-all duration-300 group relative overflow-hidden ${
                        activeTab === tab.id 
                          ? 'bg-gold text-navy shadow-lg shadow-gold/30' 
                          : 'text-foreground/60 hover:bg-gold/5 hover:text-gold'
                      }`}
                    >
                      <div className="flex items-center gap-4 relative z-10 text-left">
                        <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 transition-all ${
                          activeTab === tab.id ? 'bg-navy/10 text-navy' : 'bg-gold/10 text-gold-dark dark:text-gold'
                        }`}>
                          <tab.icon className="h-5 w-5" />
                        </div>
                        <div>
                          <span className="font-black text-sm block leading-none mb-1">{tab.label}</span>
                          <span className={`text-[10px] block opacity-60 leading-tight font-bold ${activeTab === tab.id ? 'text-navy' : ''}`}>{tab.description}</span>
                        </div>
                      </div>
                      <ChevronRight className={`h-4 w-4 relative z-10 transition-all ${
                        activeTab === tab.id ? 'text-navy translate-x-1' : 'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'
                      }`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Action Mini Card */}
              <Card className="mt-8 bg-navy dark:bg-navy-dark text-white rounded-[2.5rem] p-8 border-none shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full -translate-y-12 translate-x-12 blur-3xl group-hover:scale-125 transition-transform duration-700" />
                <div className="relative z-10">
                  <h4 className="font-black text-lg mb-2">Member Pro?</h4>
                  <p className="text-white/60 text-xs font-bold leading-relaxed mb-6">Nikmati layanan jalur hijau untuk semua perizinan konstruksi Anda.</p>
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 rounded-xl font-black text-[10px] uppercase tracking-widest h-10">
                    Pelajari Membership
                  </Button>
                </div>
              </Card>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-grow max-w-4xl">
            <AnimatePresence mode="wait">
              {renderContent()}
            </AnimatePresence>
          </div>
          
        </div>
      </div>
    </div>
  );
}
